/**
 * Compositional pane layout components for sidebar panes
 * Provides a consistent structure for pane content with flexible composition
 * Similar to IndexLayout but designed for sidebar/modal panes
 */

import * as React from "react";
import { cn } from "../../lib/utils";
import { ScrollArea } from "../ui/scroll-area";

const PaneLayout = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col h-full w-full", className)}
    {...props}
  >
    {children}
  </div>
));
PaneLayout.displayName = "PaneLayout";

const PaneLayoutEntityHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div ref={ref} className={cn("flex-shrink-0", className)} {...props}>
    {children}
  </div>
));
PaneLayoutEntityHeader.displayName = "PaneLayoutEntityHeader";

/**
 * PaneLayoutHeader - Compositional header container for panes
 * Provides consistent styling for pane headers
 */
const PaneLayoutHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    children: React.ReactNode;
  }
>(({ className, children, ...props }, ref) => {
  // Extract children by type for proper layout
  let icon = null;
  let title = null;
  let description = null;
  let actions = null;

  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const displayName = (child.type as any).displayName;
    if (displayName === "PaneLayoutHeaderIcon") icon = child;
    else if (displayName === "PaneLayoutHeaderTitle") title = child;
    else if (displayName === "PaneLayoutHeaderDescription") description = child;
    else if (displayName === "PaneLayoutHeaderActions") actions = child;
  });

  const hasContent = !!(icon || title || description);

  // Toolbar mode: actions-only headers (e.g. detail panes that just want a
  // back button + a few actions) render as a single centered flex row.
  // The original two-row layout creates an empty content row underneath
  // when only `actions` is supplied, which throws vertical centering off.
  if (actions && !hasContent) {
    return (
      <div
        ref={ref}
        className={cn(
          "relative flex h-[49px] shrink-0 items-center border-b px-3",
          className,
        )}
        {...props}
      >
        {actions}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      className={cn("relative overflow-hidden p-6 border-b", className)}
      {...props}
    >
      <div className="relative">
        {actions && <div className="flex justify-end ">{actions}</div>}
        {hasContent && (
          <div
            className={cn("flex items-center gap-3", {
              "items-start": description,
            })}
          >
            {icon}
            <div className="flex-1 min-w-0">
              {title}
              {description}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
PaneLayoutHeader.displayName = "PaneLayoutHeader";

/**
 * PaneLayoutHeaderIcon - Icon slot for pane headers
 */
const PaneLayoutHeaderIcon = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    children: React.ReactElement;
    color?: "blue" | "purple" | "green" | "orange" | "red" | "indigo" | "gray";
  }
>(({ className, children, color = "blue", ...props }, ref) => {
  const colorClasses = {
    blue: "bg-gradient-to-br from-rust/10 to-rust/10 text-teal",
    purple: "bg-gradient-to-br from-plum/10 to-purple-600/10 text-plum",
    green:
      "bg-gradient-to-br from-lc-active/10 to-spectrum-green/10 text-lc-active",
    orange: "bg-gradient-to-br from-rust/10 to-rust/10 text-orange-600",
    red: "bg-gradient-to-br from-lc-blocked/10 to-spectrum-red/10 text-lc-blocked",
    indigo:
      "bg-gradient-to-br from-spectrum-indigo/10 to-spectrum-indigo-dark/10 text-spectrum-indigo-dark",
    gray: "bg-neutral-100 dark:bg-neutral-900/20 text-neutral-500",
  };

  return (
    <div
      ref={ref}
      className={cn(
        "w-12 h-12 rounded-xl flex items-center justify-center shadow-sm",
        colorClasses[color],
        className,
      )}
      {...props}
    >
      <div>
        {React.cloneElement(children as React.ReactElement<any>, {
          className: cn(
            "h-6 w-6",
            (children as React.ReactElement<any>).props.className,
          ),
        })}
      </div>
    </div>
  );
});
PaneLayoutHeaderIcon.displayName = "PaneLayoutHeaderIcon";

/**
 * PaneLayoutHeaderTitle - Title slot for pane headers
 */
const PaneLayoutHeaderTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn("text-xl font-semibold", className)} {...props} />
));
PaneLayoutHeaderTitle.displayName = "PaneLayoutHeaderTitle";

/**
 * PaneLayoutHeaderDescription - Description/metadata slot for pane headers
 */
const PaneLayoutHeaderDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
PaneLayoutHeaderDescription.displayName = "PaneLayoutHeaderDescription";

/**
 * PaneLayoutHeaderActions - Action buttons slot for pane headers
 */
const PaneLayoutHeaderActions = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-2 w-full sm:w-auto flex-shrink-0",
      className,
    )}
    {...props}
  >
    <div id="pane-layout-header-actions-slot" />
    {children}
  </div>
));
PaneLayoutHeaderActions.displayName = "PaneLayoutHeaderActions";

const PaneLayoutContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { noScroll?: boolean }
>(({ className, children, noScroll = false, ...props }, ref) =>
  noScroll ? (
    <div
      ref={ref}
      className={cn("flex-1 min-h-0 flex flex-col px-6 pt-4 pb-6", className)}
      {...props}
    >
      <div className="min-h-0 flex-1 flex flex-col w-full scrollbar-hide overflow-hidden">
        {children}
      </div>
    </div>
  ) : (
    // Radix ScrollArea wraps content in a `display:table; min-width:100%`
    // viewport div, which sizes to `max-content` — so a long, unbreakable
    // child (e.g. an entity card with a long filename) widens the content
    // past the pane and defeats `truncate`. Force that viewport child to
    // block-level so content is clamped to the pane width (panes scroll
    // vertically, never horizontally).
    <ScrollArea className="flex-1 h-full [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div
        ref={ref}
        className={cn("flex-1 h-full flex flex-col px-6 pt-4 pb-6", className)}
        {...props}
      >
        <div className="h-full flex flex-col w-full scrollbar-hide overflow-visible">
          {children}
        </div>
      </div>
    </ScrollArea>
  ),
);
PaneLayoutContent.displayName = "PaneLayoutContent";

export {
  PaneLayout,
  PaneLayoutContent,
  PaneLayoutEntityHeader,
  PaneLayoutHeader,
  PaneLayoutHeaderActions,
  PaneLayoutHeaderDescription,
  PaneLayoutHeaderIcon,
  PaneLayoutHeaderTitle,
};
